/** Reclaim candidates, as counts.
 *
 *  **Aggregates only, by design.** Which accounts these numbers refer to is
 *  employee personal data, so it lives behind an admin-only route and an
 *  admin-only response. This panel links there; it never inlines the list. */

import { Icon, Users } from "@norican/icons";
import { Badge, Button, Card, StatisticBar, Typography } from "@norican/react";
import { useNavigate } from "react-router";

import type { ReclaimableSummary as ReclaimableSummaryData } from "@/lib/api";

import { formatSeats } from "./licence-format";

/** Where the admin-only identity drill-down is mounted in `App.tsx`. */
export const RECLAIMABLE_ACCOUNTS_PATH = "/licences/accounts";

interface ReclaimableSummaryProps {
  summary: ReclaimableSummaryData;
  /** Whether the viewer holds the admin role — controls the link, not access. */
  isAdmin: boolean;
}

export function ReclaimableSummary({ summary, isAdmin }: ReclaimableSummaryProps) {
  const navigate = useNavigate();

  return (
    <Card variant="outline" accent="left" accentTone="warning">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <Typography variant="heading-4" as="h2" className="flex items-center gap-2">
            <Icon icon={Users} size="sm" />
            Reclaimable seats
          </Typography>
          <Badge tone="warning" variant="subtle" size="sm">
            {formatSeats(summary.reclaimable_seats)} candidate seats
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <StatisticBar
          size="md"
          aria-label="Licence reclaim candidates"
          items={[
            {
              label: "Licensed users",
              value: formatSeats(summary.licensed_users),
            },
            {
              label: "Disabled but licensed",
              value: formatSeats(summary.disabled_licensed),
              tone: summary.disabled_licensed > 0 ? "negative" : "neutral",
              caption: "Accounts switched off, licence still assigned",
            },
            {
              label: "Guests with licences",
              value: formatSeats(summary.guest_licensed),
              tone: summary.guest_licensed > 0 ? "caution" : "neutral",
            },
            {
              label: "Inactive 90d+",
              value: summary.sign_in_data_available ? formatSeats(summary.inactive_90d) : "—",
              tone: summary.inactive_90d > 0 ? "caution" : "neutral",
              caption: summary.sign_in_data_available
                ? `${formatSeats(summary.never_signed_in)} never signed in`
                : "Needs AuditLog.Read.All",
              align: "end",
            },
          ]}
        />
      </Card.Body>
      <Card.Footer>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-fg-muted text-xs">
            Counts only. Which accounts these are is employee personal data and is restricted to
            administrators.
          </p>
          {isAdmin ? (
            <Button
              variant="outline"
              tone="neutral"
              size="sm"
              onClick={() => navigate(RECLAIMABLE_ACCOUNTS_PATH)}
            >
              View accounts
            </Button>
          ) : (
            <span className="text-fg-subtle text-xs">Admin access required</span>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
}
